const DEFAULT_CHART_DATA = [
  { label: "1", incoming: 58, answer: 42, shadow: 50 },
  { label: "2", incoming: 59, answer: 41, shadow: 49 },
  { label: "3", incoming: 46, answer: 40, shadow: 44 },
  { label: "4", incoming: 74, answer: 56, shadow: 62 },
  { label: "5", incoming: 34, answer: 28, shadow: 31 },
  { label: "6", incoming: 47, answer: 36, shadow: 41 },
  { label: "7", incoming: 83, answer: 62, shadow: 70 },
  { label: "8", incoming: 71, answer: 50, shadow: 58 },
];

const DEFAULT_METRICS = [
  { key: "talk-time", label: "AVG TALK TIME", value: "3mins 52s" },
  { key: "wait-time", label: "AVG WAIT TIME", value: "1min 6s" },
  { key: "answered-calls", label: "ANSWERED CALLS", value: "99.58%" },
];

function getChartPoints(values, width, height, padding, minValue, maxValue) {
  const usableWidth = width - padding.left - padding.right;
  const usableHeight = height - padding.top - padding.bottom;
  const stepX = values.length > 1 ? usableWidth / (values.length - 1) : 0;
  const range = Math.max(maxValue - minValue, 1);

  return values.map((value, index) => {
    const x = padding.left + stepX * index;
    const y =
      padding.top + ((maxValue - value) / range) * usableHeight;

    return { x, y };
  });
}

function buildSmoothPath(points) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let d = `M ${points[0].x} ${points[0].y}`;

  for (let i = 1; i < points.length - 1; i += 1) {
    const current = points[i];
    const next = points[i + 1];
    const midX = (current.x + next.x) / 2;
    const midY = (current.y + next.y) / 2;
    d += ` Q ${current.x} ${current.y} ${midX} ${midY}`;
  }

  const last = points[points.length - 1];
  d += ` T ${last.x} ${last.y}`;

  return d;
}

export default function PerformanceCard({
  chartData = DEFAULT_CHART_DATA,
  metrics = DEFAULT_METRICS,
}) {
  const width = 420;
  const height = 130;
  const padding = { top: 14, right: 6, bottom: 14, left: 6 };

  const incomingValues = chartData.map((item) => item.incoming ?? 0);
  const answerValues = chartData.map((item) => item.answer ?? 0);
  const shadowValues = chartData.map(
    (item) => item.shadow ?? Math.round(((item.incoming ?? 0) + (item.answer ?? 0)) / 2)
  );

  const allValues = [...incomingValues, ...answerValues, ...shadowValues];
  
  // FIX: Dynamic scaling based on actual data range
  const minValue = Math.min(...allValues, 0);
  const maxValue = Math.max(...allValues);
  
  // Add 10% padding to the top and bottom for better visualization
  const valueRange = maxValue - minValue;
  const paddingPercent = 0.1; // 10% padding
  const adjustedMin = Math.max(minValue - (valueRange * paddingPercent), 0);
  const adjustedMax = maxValue + (valueRange * paddingPercent);
  
  // Ensure we have a valid range (avoid division by zero)
  const finalMin = adjustedMin;
  const finalMax = Math.max(adjustedMax, adjustedMin + 1);

  const incomingPath = buildSmoothPath(
    getChartPoints(incomingValues, width, height, padding, finalMin, finalMax)
  );

  const answerPath = buildSmoothPath(
    getChartPoints(answerValues, width, height, padding, finalMin, finalMax)
  );

  const shadowPath = buildSmoothPath(
    getChartPoints(shadowValues, width, height, padding, finalMin, finalMax)
  );

  return (
    <div className="performance-card">
      <div className="performance-card-head">
        <div className="performance-card-title">Performance</div>

        <div className="performance-legend">
          <div className="performance-legend-item">
            <span className="legend-dot incoming" />
            <span>Incoming calls</span>
          </div>
          <div className="performance-legend-item">
            <span className="legend-dot answer" />
            <span>Answer</span>
          </div>
        </div>
      </div>

      <div className="performance-chart">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="performance-svg"
          preserveAspectRatio="none"
        >
          <path d={shadowPath} className="performance-line shadow" />
          <path d={incomingPath} className="performance-line incoming" />
          <path d={answerPath} className="performance-line answer" />
        </svg>
      </div>

      <div className="performance-metrics">
        {metrics.map((item) => (
          <div className="performance-metric" key={item.key}>
            <div className="performance-metric-label">{item.label}</div>
            <div className="performance-metric-value">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
